import { PageLayout } from '@repo/ui/components/page-layout'
import { QueryBreadcrumbs } from '@repo/ui/components/query-breadcrumbs'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from '@repo/ui/empty'
import { ScrollArea } from '@repo/ui/scroll-area'
import { Skeleton } from '@repo/ui/skeleton'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CloudOff, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from '@/lib/i18n'
import { InternalLinkSection } from '@/routes/(private)/(shell)/admin/policies/-components/internal-link-section'
import { PolicySettingCard } from '@/routes/(private)/(shell)/admin/policies/-components/policy-setting-card'
import { PublicLinksSection } from '@/routes/(private)/(shell)/admin/policies/-components/public-links-section'
import { RetentionSection } from '@/routes/(private)/(shell)/admin/policies/-components/retention-section'
import { WhoCanShareSection } from '@/routes/(private)/(shell)/admin/policies/-components/who-can-share-section'
import type { ClassifiedPolicy } from '@/routes/(private)/(shell)/admin/policies/-lib/policy-settings'
import { organizationPoliciesQueryOptions } from '@/routes/(private)/(shell)/admin/policies/-query/query-options'

export const Route = createFileRoute('/(private)/(shell)/admin/policies/')({
	component: AdminPolicies,
	staticData: {
		breadcrumb: 'admin-policies:page_title'
	}
})

/**
 * Every rule the organization runs by, on one page: who may share, what a
 * public link may do and how long it lasts, and how long the audit trail is kept.
 *
 * It was two screens until OPSSTD-9025 — «Condivisione» for sharing and links,
 * «Regole» for retention — each nearly empty and each filtering the same
 * `GET /policies` for its own keys. One read now feeds every section.
 *
 * **"DID SOMEBODY CHOOSE THIS?" IS ANSWERED BY `isOverridden`**, the server's
 * own record of whether a row exists, never by comparing the value to the
 * default: the two are equal both when nobody touched a rule and when somebody
 * deliberately picked the number the instance ships with.
 *
 * **NO ROLE CHECK HERE.** The area layout one level up already refuses anybody
 * who is not an organization administrator, once, for every slice beneath it.
 * Repeating it would suggest the check is per page and that a page without one
 * is open.
 *
 * Sign-in credentials are not here: password, MFA and lockout rules live on
 * the identity provider, and a section that only says so is not a rule the
 * administrator runs.
 */
function AdminPolicies() {
	const { t } = useTranslation()
	const policies = useQuery(organizationPoliciesQueryOptions())

	const sharing = findPolicy(policies.data, 'sharing')
	const internalLink = findPolicy(policies.data, 'link_internal')
	const linkAllowed = findPolicy(policies.data, 'link_allowed')
	const linkRequirePassword = findPolicy(
		policies.data,
		'link_require_password'
	)
	const linkMaxExpiry = findPolicy(policies.data, 'link_max_expiry_days')
	const linkDefaultExpiry = findPolicy(
		policies.data,
		'link_default_expiry_days'
	)
	const retention = findPolicy(policies.data, 'retention')
	const unrecognized = policies.data?.filter(
		(policy) => policy.kind === 'unrecognized'
	)

	return (
		<PageLayout breadcrumb={<QueryBreadcrumbs />}>
			<ScrollArea className='min-h-0 flex-1'>
				<div className='flex flex-col px-4 pb-6 md:px-6'>
					{/*
					 * Offline before loading: with `networkMode: 'online'` a
					 * query the browser cannot send is pending and *not*
					 * fetching, so a skeleton gated on the pending flag alone
					 * would spin forever without a request behind it.
					 */}
					{policies.isPaused && (
						<PoliciesNotice
							description={t(
								'admin-policies:offline.description'
							)}
							icon={<CloudOff />}
							title={t('admin-policies:offline.title')}
						/>
					)}

					{!policies.isPaused && policies.isPending && (
						<div className='flex flex-col gap-4 py-7'>
							<Skeleton className='h-32 w-full rounded-xl' />
							<Skeleton className='h-32 w-full rounded-xl' />
						</div>
					)}

					{policies.isError && (
						<PoliciesNotice
							description={t(
								'admin-policies:unavailable.description'
							)}
							icon={<TriangleAlert />}
							title={t('admin-policies:unavailable.title')}
						/>
					)}

					<div className='flex flex-col divide-y'>
						{sharing && <WhoCanShareSection policy={sharing} />}

						{internalLink && (
							<InternalLinkSection policy={internalLink} />
						)}

						{linkAllowed &&
							linkRequirePassword &&
							linkMaxExpiry &&
							linkDefaultExpiry && (
								<PublicLinksSection
									allowed={linkAllowed}
									defaultExpiry={linkDefaultExpiry}
									maxExpiry={linkMaxExpiry}
									requirePassword={linkRequirePassword}
								/>
							)}

						{retention && <RetentionSection policy={retention} />}
					</div>

					{/*
					 * A rule this build has no editor for is still a rule the
					 * organization runs by: the registry is a key/value table
					 * so a later release adds one without a migration, and it
					 * reaches this page before an editor does. Hiding it would
					 * make it invisible to the person it governs.
					 */}
					{unrecognized?.map((policy) => (
						<div className='pt-7' key={policy.key}>
							<PolicySettingCard
								description={t(
									'admin-policies:unrecognized.description'
								)}
								fallback={JSON.stringify(policy.fallback)}
								isOverridden={policy.isOverridden}
								title={policy.describe}
								value={JSON.stringify(policy.value)}
							/>
						</div>
					))}
				</div>
			</ScrollArea>
		</PageLayout>
	)
}

function PoliciesNotice({
	icon,
	title,
	description
}: {
	icon: ReactNode
	title: string
	description: string
}) {
	return (
		<Empty>
			<EmptyHeader>
				<EmptyMedia variant='icon'>{icon}</EmptyMedia>
				<EmptyTitle>{title}</EmptyTitle>
				<EmptyDescription>{description}</EmptyDescription>
			</EmptyHeader>
		</Empty>
	)
}

function findPolicy<K extends ClassifiedPolicy['kind']>(
	policies: ClassifiedPolicy[] | undefined,
	kind: K
) {
	return policies?.find(
		(policy): policy is Extract<ClassifiedPolicy, { kind: K }> =>
			policy.kind === kind
	)
}
